import { useQuery as useTopCategoryQuery } from "@faire/web-api/api/brand/brandToken/top-category/get-hooks";
import { useQuery as useBrandViewQuery } from "@faire/web-api/api/v2/brand-view/brandToken/get-hooks";
import { getQueryKey_escape_hatch as getQueryKeySearchProductsFromBrand } from "@faire/web-api/api/v2/search/products/from-brand/post-hooks";
import {
  getIsoToFromCountryNames,
  getIsoToFromCountryNamesSorted,
} from "@faire/web/common/consts/ISO_TO_FROM_COUNTRY_NAME";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useLocalizationStore } from "@faire/web/common/localization/LocalizationStore";
import { logAssert } from "@faire/web/common/logging";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Spacer } from "@faire/web/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Breadcrumb } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(pagesWithTopOfPageContent)/brand/[brandToken]/(rsc)/rsc/_sections/Breadcrumb/Breadcrumb";
import { getBrandAboutSubheader } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(pagesWithTopOfPageContent)/brand/[brandToken]/(rsc)/rsc/_sections/Info/Story/getBrandAboutSubheader";
import { FaireHelmet } from "@faire/retailer/components/Helmet";
import { useBrandFilterSectionsData } from "@faire/retailer/components/LoggedOutBrand/hooks/useBrandFilterSectionsData";
import { useBrandLeanProductTilesData } from "@faire/retailer/components/LoggedOutBrand/hooks/useBrandLeanProductTilesData";
import { PageInitialized } from "@faire/retailer/events";
import { readBundledData } from "@faire/retailer/lib/bundledData";
import { useAssignSettingFaireDirectSignUpFromMarketplaceEffect } from "@faire/retailer/settings/useSettingFaireDirectSignUpFromMarketplace";
import { IProps } from "@faire/retailer/views/brand/logged-out";
import { useBrandPageQueryParams } from "@faire/retailer/views/brand/logged-out/__internal__/useBrandPageQueryParams";
import { BrandInfo } from "@faire/retailer/views/brand/logged-out/components/BrandInfo";
import { LoggedOutBrandPageSkeleton } from "@faire/retailer/views/brand/logged-out/components/LoggedOutBrandPageSkeleton";
import { TestimonialModule } from "@faire/retailer/views/brand/logged-out/components/TestimonialModule";
import {
  getMetaDescription,
  getMetaTitle,
} from "@faire/retailer/views/brand/logged-out/shared/metaTagText";

import { BelowTheFold } from "./components/BelowTheFold";
import { ClientEffects } from "./components/ClientEffects";
import { SCROLL_PATH } from "./shared/consts";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
const STALE_TIME = 1000 * 60 * 5;

const LoggedOutBrandBase: React.FC<IProps> = ({ brandToken }) => {
  const { strictLocalize } = useStrictLocalization();
  const { localeKey } = useLocalizationStore(["localeKey"]);

  const {
    data: brandViewData,
    isPending: brandViewIsPending,
    dataUpdatedAt: brandViewDataUpdatedAt,
  } = useBrandViewQuery(brandToken, undefined, {
    initialData: () => {
      const bundle = readBundledData("brand_view_v2", { keep: true });
      if (bundle?.brand?.token === brandToken) {
        return bundle;
      }
      return undefined;
    },
    staleTime: STALE_TIME,
  });

  const brand = brandViewData?.brand;
  const results = getIsoToFromCountryNames(strictLocalize);
  type IsoCode = keyof typeof results;

  const { push } = useHistory();
  const { selectedCategoryFilterKey, refP } = useBrandPageQueryParams(push);

  const {
    searchProductsFromBrandRequest,
    brandLeanProductTilesData,
    brandLeanProductTilesIsPending,
    brandLeanProductTilesDataUpdatedAt,
  } = useBrandLeanProductTilesData({
    brandToken,
    selectedCategoryFilterKey,
    refP,
  });

  const { filterSections, categoryFilterOptions } = useBrandFilterSectionsData({
    brandToken,
  });

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "page-brand-logged-out",
    pageLoadKey: JSON.stringify(
      getQueryKeySearchProductsFromBrand(searchProductsFromBrandRequest)
    ),
    dataUpdatedAt: Math.max(
      brandViewDataUpdatedAt,
      brandLeanProductTilesDataUpdatedAt
    ),
    numComponents: 2,
    isPageLoading: brandViewIsPending || brandLeanProductTilesIsPending,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
  });

  const { data: topCategoryData } = useTopCategoryQuery(brandToken);

  const brandInfoRef = useDoActionWhenVisible(() => {
    trackEvent(`${SCROLL_PATH}/main-content`);
  });
  const testimonialRef = useDoActionWhenVisible(() => {
    trackEvent(`${SCROLL_PATH}/testimonial`);
  });

  useEffect(() => {
    if (!brandViewIsPending && !brandLeanProductTilesIsPending) {
      PageInitialized.publish();
    }
  }, [brandViewIsPending, brandLeanProductTilesIsPending]);

  useAssignSettingFaireDirectSignUpFromMarketplaceEffect();

  const description = useMemo(() => {
    if (!brand?.name) {
      return null;
    }

    const fromCountry =
      getIsoToFromCountryNamesSorted(strictLocalize)[
        brand?.based_in as IsoCode
      ];

    return getMetaDescription(
      {
        brandCompanyName: brand.name,
        categoryFilterOptions,
        fromCountry,
      },
      strictLocalize
    );
  }, [brand?.name, brand?.based_in, categoryFilterOptions, strictLocalize]);

  if (brandViewIsPending) {
    return <LoggedOutBrandPageSkeleton />;
  }

  logAssert(
    !!brand,
    "If the call for brand-view is fetched (inferred from !brandViewIsPending) then expect brand to be defined."
  );

  const title = getMetaTitle(
    {
      brandCompanyName: brand.name,
    },
    strictLocalize
  );

  const brandAboutSubheader = getBrandAboutSubheader(
    brand,
    filterSections ?? [],
    localeKey,
    strictLocalize
  );

  return (
    <>
      <FaireHelmet
        title={title}
        description={description}
        alignOpenGraphTitleDescription
      />
      <Breadcrumb brandName={brand.name} topCategory={topCategoryData} />
      <BrandInfo
        brand={brand}
        productTiles={brandLeanProductTilesData?.product_tiles}
        productTilesIsLoading={brandLeanProductTilesIsPending}
        productsRequestId={brandLeanProductTilesData?.request_id}
        totalProductsCount={
          brandLeanProductTilesData?.pagination_data?.total_results
        }
        showVerticalImagery={
          !!brandLeanProductTilesData?.should_show_vertical_image
        }
        loadTimingRef={loadTimingRef}
        brandAboutSubheader={brandAboutSubheader}
        categoryFilterOptions={categoryFilterOptions}
        ref={brandInfoRef}
      />
      <Spacer height="5x" />
      <TestimonialModule ref={testimonialRef} />
      <Spacer height="7x" />
      <BelowTheFold
        brandToken={brandToken}
        brand={brand}
        topCategory={topCategoryData}
      />
      <ClientEffects brandToken={brandToken} />
    </>
  );
};

export const LoggedOutBrand: React.FC<IProps> = (props) => {
  return <LoggedOutBrandBase {...props} />;
};

export default LoggedOutBrand;

LoggedOutBrand.displayName = "LoggedOutBrandPage";
